import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, tasksAPI } from '../services/api';
import api from '../services/api';
import type { Task } from '../types';
import Navigation from '../components/Navigation';
import WeeklyCalendar from '../components/WeeklyCalendar';
import MonthlyCalendar from '../components/MonthlyCalendar';
import MonthCalendarView from '../components/MonthCalendarView';
import TaskModal from '../components/TaskModal';
import Toast from '../components/Toast';
import { formatUserTimezone, getUserTimezoneGMT } from '../utils/timezone';

type ViewMode = 'week' | 'month';
type ToastType = 'success' | 'error' | 'info' | 'warning';

const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState<any>(null); // Temporary for working hours display
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<{ date: Date; time: string } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [editingWorkingHours, setEditingWorkingHours] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  
  // For Owner/Admin: user selection
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null | 'all'>(null);
  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedUserId]);

  // Reload data when coming back from settings (only if URL changed)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only reload if page becomes visible after being hidden for more than 30 seconds
      // This prevents unnecessary reloads when just scrolling
      if (!document.hidden) {
        // Don't auto-reload - let user refresh manually if needed
        // Or reload only if coming from a different page (check document.referrer)
        const referrer = document.referrer;
        if (referrer && !referrer.includes(window.location.origin)) {
          // Coming from external page - reload
      loadCalendarData();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      
      // Load current user
      const userData = await authAPI.getCurrentUser();
      console.log('Current user data:', userData);
      
      let targetUserId = userData.id;
      let targetUser = userData;
      let managers: any[] = [];
      
      // For Owner/Admin: load all managers and select one to view
      if ((userData as any).can_view_all_tasks) {
        console.log('Owner/Admin mode - loading all managers');
        
        // Load all users (to get managers list)
        const usersResponse = await api.get('/api/users/');
        console.log('Users API response:', usersResponse.data);
        
        // Handle both array and paginated response
        let allUsers: any[] = [];
        if (Array.isArray(usersResponse.data)) {
          allUsers = usersResponse.data;
        } else if (usersResponse.data && typeof usersResponse.data === 'object' && 'results' in usersResponse.data) {
          allUsers = (usersResponse.data as any).results || [];
        }
        
        console.log('All users loaded:', allUsers.length);
        managers = allUsers.filter((u: any) => u.role === 'manager');
        
        setAvailableUsers(managers);
        console.log('Available managers:', managers.length, managers);
        
        // Handle "all" selection or specific manager
        if (selectedUserId === 'all') {
          // Viewing all managers - use first manager for schedule display (working hours will be merged)
          if (managers.length > 0) {
            targetUser = managers[0];
            targetUserId = 'all' as any; // Special marker
          } else {
            setToast({ message: 'ℹ️ No managers found. Add managers to see their calendars.', type: 'info' });
            setSchedule({ id: 0, name: 'No Managers', working_hours: [], working_hours_exceptions: {}, manager: userData, slot_duration_minutes: 30, priority: 1, is_active: true, google_calendar_sync_enabled: false, google_calendar_sync_interval_minutes: 15, timezone: 'UTC', created_at: new Date().toISOString(), updated_at: new Date().toISOString() } as any);
            setLoading(false);
            return;
          }
        } else if (selectedUserId && typeof selectedUserId === 'number') {
          targetUserId = selectedUserId;
          const foundManager = managers.find((u: any) => u.id === selectedUserId);
          if (foundManager) {
            targetUser = foundManager;
          } else if (managers.length > 0) {
            targetUser = managers[0];
            targetUserId = managers[0].id;
            setSelectedUserId(managers[0].id);
          }
        } else if (managers.length > 0) {
          // Default to first manager if nothing selected
          targetUserId = managers[0].id;
          targetUser = managers[0];
          setSelectedUserId(managers[0].id);
        } else {
          setToast({ message: 'ℹ️ No managers found. Add managers to see their calendars.', type: 'info' });
          setSchedule({ id: 0, name: 'No Managers', working_hours: [], working_hours_exceptions: {}, manager: userData, slot_duration_minutes: 30, priority: 1, is_active: true, google_calendar_sync_enabled: false, google_calendar_sync_interval_minutes: 15, timezone: 'UTC', created_at: new Date().toISOString(), updated_at: new Date().toISOString() } as any);
          setLoading(false);
          return;
        }
        
        console.log('Viewing calendar for user:', targetUser, 'selectedUserId:', selectedUserId);
      }
      
      // Get working hours - if "all", merge all managers' working hours
      let loadedWorkingHours: any[] = [];
      let loadedExceptions: any = {};
      
      if (selectedUserId === 'all' && (userData as any).can_view_all_tasks && managers.length > 0) {
        // Merge working hours from all managers
        // Merge working hours: a slot is available if ANY manager has it available
        const mergedHours: any[] = [];
        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
          const dayHours: any = {
            weekday: dayIndex,
            is_active: false,
            ranges: []
          };
          
          // Collect all ranges from all managers for this day
          const allRanges: Array<{ start: string; end: string }> = [];
          managers.forEach((mgr: any) => {
            const mgrHours = mgr.working_hours || [];
            const dayData = mgrHours.find((h: any) => h.weekday === dayIndex);
            if (dayData && dayData.is_active && dayData.ranges) {
              dayHours.is_active = true;
              allRanges.push(...dayData.ranges);
            }
          });
          
          // Merge overlapping ranges
          if (allRanges.length > 0) {
            allRanges.sort((a, b) => a.start.localeCompare(b.start));
            const merged: Array<{ start: string; end: string }> = [];
            let current = { ...allRanges[0] };
            
            for (let i = 1; i < allRanges.length; i++) {
              if (allRanges[i].start <= current.end) {
                current.end = allRanges[i].end > current.end ? allRanges[i].end : current.end;
              } else {
                merged.push(current);
                current = { ...allRanges[i] };
              }
            }
            merged.push(current);
            dayHours.ranges = merged;
          }
          
          mergedHours.push(dayHours);
        }
        
        loadedWorkingHours = mergedHours;
        // For "all" view, we don't show exceptions (too complex to merge)
        loadedExceptions = {};
      } else {
        // Single manager view
        loadedWorkingHours = targetUser.working_hours || [];
        loadedExceptions = targetUser.working_hours_exceptions || {};
      }
      
      console.log('Target user working hours:', loadedWorkingHours);
      console.log('Target user exceptions:', loadedExceptions);
      
      // Create temp schedule for working hours display
      const scheduleName = selectedUserId === 'all' 
        ? 'All Managers Calendar' 
        : (targetUser.first_name ? `${targetUser.first_name}'s Calendar` : 'Calendar');
      
      const tempSchedule: any = {
        id: 0,
        name: scheduleName,
        manager: targetUser,
        slot_duration_minutes: 30,
        priority: 1,
        is_active: true,
        google_calendar_sync_enabled: false,
        google_calendar_sync_interval_minutes: 15,
        timezone: 'UTC',
        working_hours: loadedWorkingHours,
        working_hours_exceptions: loadedExceptions,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setSchedule(tempSchedule);
      
      // Load tasks - include blocked time (tasks without email) for calendar view
      // Need to load ALL pages for calendar view
      let allTasks: any[] = [];
      let currentUrl: string | null = '/api/tasks/?include_blocked_time=true';
      
      while (currentUrl) {
        const response: any = await api.get(currentUrl);
        const data: any = response.data;
        
        if (Array.isArray(data)) {
          // If response is array, we got all tasks
          allTasks = allTasks.concat(data);
          currentUrl = null;
        } else if (data && typeof data === 'object' && 'results' in data) {
          // Paginated response
          allTasks = allTasks.concat(data.results || []);
          // Extract next URL - it might be full URL or relative path
          if (data.next) {
            try {
              const urlObj: URL = new URL(data.next);
              currentUrl = urlObj.pathname + urlObj.search;
            } catch {
              // If parsing fails, try using it as relative path
              currentUrl = data.next.startsWith('http') ? null : data.next;
            }
          } else {
            currentUrl = null;
          }
        } else {
          currentUrl = null;
        }
      }
      
      console.log('Tasks API response: loaded', allTasks.length, 'tasks total');
      
      // Handle both array and paginated response
      let userTasks: any[] = allTasks;
      
      // For Owner/Admin: filter tasks by selected user (unless "all" is selected)
      if ((userData as any).can_view_all_tasks && selectedUserId !== 'all' && targetUserId && typeof targetUserId === 'number') {
        console.log('Filtering tasks for user ID:', targetUserId);
        console.log('All tasks before filter:', userTasks.length);
        
        // Debug: Show all task managers
        userTasks.forEach((task: any, idx: number) => {
          const managerId = typeof task.manager === 'object' ? task.manager.id : task.manager;
          console.log(`Task ${idx}: manager=${task.manager} (${typeof task.manager}), extracted ID=${managerId}, match=${managerId === targetUserId}`);
        });
        
        userTasks = userTasks.filter((task: any) => {
          // task.manager can be either ID (number) or object {id: X}
          const managerId = typeof task.manager === 'object' ? task.manager.id : task.manager;
          return managerId === targetUserId;
        });
        console.log(`Filtered to ${userTasks.length} tasks for user ${targetUserId}`);
      } else if (selectedUserId === 'all') {
        console.log('Showing all managers\' tasks:', userTasks.length);
      }
      
      setTasks(userTasks);
      console.log(`Loaded ${userTasks.length} tasks`);
    } catch (err: any) {
      console.error('Calendar error:', err);
      setSchedule(null);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkingHour = async (date: Date, time: string) => {
    if (!user) return;

    try {
      // Format date as YYYY-MM-DD
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

      console.log(`🔧 Toggling exception for SPECIFIC date: ${dateStr} at ${time}`);

      // Toggle this specific date/time
      const result = await authAPI.toggleWorkingHourException(dateStr, time);
      
      console.log('Toggle result:', result);
      
      // Reload calendar
      await loadCalendarData();
      
      setToast({ 
        message: `✓ ${dayName} ${dateStr} ${time} - ${result.action}`, 
        type: 'success' 
      });
    } catch (err: any) {
      console.error('Failed to update working hours:', err);
      setToast({ message: '✗ Failed to update working hours', type: 'error' });
    }
  };

  const handleSlotClick = (date: Date, time: string) => {
    // time is already in UTC format (HH:MM) after conversion in WeeklyCalendar
    // date is a Date object representing the local date (but may have timezone issues)
    // We need to create a proper date object that represents the correct UTC date
    
    // Extract date components from the local date (year, month, day)
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Create a new Date object using UTC components to avoid timezone shifts
    // This ensures the date doesn't shift when we convert it later
    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
    
    console.log('Slot clicked:', {
      originalDate: date,
      utcDate: utcDate,
      time: time,
      'Editing mode': editingWorkingHours,
      'Local date string': date.toLocaleDateString(),
      'UTC date string': utcDate.toISOString().split('T')[0]
    });
    
    // Owner/Admin viewing manager's calendar - can create tasks for that manager
    // Note: if selectedUserId === 'all', user must select a specific manager when creating task
    const isViewingManagerCalendar = user && user.can_view_all_tasks && selectedUserId && selectedUserId !== 'all';
    
    // Manager can edit their own working hours
    const canEditWorkingHours = user && user.has_own_calendar;
    
    if (editingWorkingHours && canEditWorkingHours) {
      // In editing mode, toggle working hours for this time slot (Manager only)
      console.log('Toggling working hours...');
      toggleWorkingHour(date, time);
      return;
    }
    
    // Normal mode: create task
    // Manager creates for themselves, Owner/Admin create for the manager they're viewing
    // If "all" is selected, show warning to select a specific manager
    if (selectedUserId === 'all') {
      setToast({
        message: '⚠ Please select a specific manager to create tasks. Use the dropdown above.',
        type: 'warning'
      });
    } else if (isViewingManagerCalendar || user?.has_own_calendar) {
      console.log('Opening task modal...');
      setSelectedSlot({ date: utcDate, time });
      setShowModal(true);
    } else {
      setToast({
        message: '⚠ Cannot create tasks. Please select a manager to view their calendar.',
        type: 'warning'
      });
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleTaskClick = (task: Task) => {
    // Prevent deletion of Google Calendar synced events
    if (task.source === 'google') {
      setToast({
        message: '⚠ Cannot delete events synced from Google Calendar',
        type: 'warning'
      });
      return;
    }
    
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    try {
      await tasksAPI.delete(selectedTask.id);
      await loadCalendarData();
      setToast({ 
        message: '✓ Task deleted successfully', 
        type: 'success' 
      });
      setShowDeleteModal(false);
      setSelectedTask(null);
    } catch (err: any) {
      console.error('Failed to delete task:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete task';
      setToast({ 
        message: `✗ ${errorMessage}`, 
        type: 'error' 
      });
    }
  };

  const handleReservationCreate = async (data: { clientName: string; clientEmail: string; description?: string }) => {
    if (!selectedSlot) return;

    try {
      const [hours, minutes] = selectedSlot.time.split(':').map(Number);
      
      // selectedSlot.date is a Date object in UTC (created from local date components)
      // selectedSlot.time is already in UTC format (HH:MM)
      // Extract UTC date components to avoid timezone issues
      const year = selectedSlot.date.getUTCFullYear();
      const month = selectedSlot.date.getUTCMonth();
      const day = selectedSlot.date.getUTCDate();
      
      // Create start time in UTC
      const startTime = new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));
      
      // Validate that start time is in the future
      const now = new Date();
      if (startTime <= now) {
        setToast({ 
          message: '✗ Cannot create task in the past. Please select a future date and time.', 
          type: 'error' 
        });
        setShowModal(false);
        setSelectedSlot(null);
        return;
      }
      
      const endTime = new Date(startTime);
      endTime.setUTCMinutes(endTime.getUTCMinutes() + 30); // Default 30-minute slots

      // Determine manager ID:
      // - For Manager: create for themselves (user.id)
      // - For Owner/Admin: create for the manager they're viewing (selectedUserId, but not 'all')
      const managerId = user?.can_view_all_tasks && selectedUserId && selectedUserId !== 'all'
        ? selectedUserId 
        : user!.id;
      
      console.log('Creating task for manager ID:', managerId);

      // Get client timezone from browser (convert to GMT format)
      const clientTimezone = getUserTimezoneGMT();

      await tasksAPI.create({
        manager: managerId,
        client_name: data.clientName,
        client_email: data.clientEmail,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        status: 'confirmed',
        notes: data.description || '',
        slot_duration_minutes: 30, // Default 30-minute slots
        priority: 1,
        is_active: true,
        timezone: 'UTC',
        user_timezone: clientTimezone, // Save client timezone for reminder emails
      });

      setShowModal(false);
      setSelectedSlot(null);
      setToast({ message: '✓ Task created successfully!', type: 'success' });
      loadCalendarData();
    } catch (err: any) {
      console.error('Failed to create task:', err);
      
      // Extract error message from response
      let errorMessage = '✗ Failed to create task';
      if (err.response?.data) {
        const errorData = err.response.data;
        // Check for non_field_errors (general validation errors)
        if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          errorMessage = `✗ ${errorData.non_field_errors[0]}`;
        } else if (errorData.error) {
          errorMessage = `✗ ${errorData.error}`;
        } else if (errorData.detail) {
          errorMessage = `✗ ${errorData.detail}`;
        } else if (typeof errorData === 'string') {
          errorMessage = `✗ ${errorData}`;
        }
      }
      
      setToast({ message: errorMessage, type: 'error' });
      setShowModal(false);
      setSelectedSlot(null);
    }
  };

  if (loading || !schedule) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin h-12 w-12 border-4 border-accent-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading calendar...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        {/* Calendar Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-3">
                    <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold border-2 border-primary-300">
                      🌍 {formatUserTimezone()}
                    </span>
                  </div>
                  {user?.can_view_all_tasks ? (
                    <div className="mt-2">
                      <label className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Viewing Manager:</label>
                      <select
                        value={selectedUserId === 'all' ? 'all' : (selectedUserId || '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'all') {
                            setSelectedUserId('all');
                          } else {
                            setSelectedUserId(Number(value));
                          }
                        }}
                        className="ml-2 px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 font-medium text-gray-900"
                      >
                        <option value="all">All Managers</option>
                        {availableUsers.map((mgr: any) => (
                          <option key={mgr.id} value={mgr.id}>
                            {mgr.first_name} {mgr.last_name} (@{mgr.username})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 mt-1">
                      {user?.first_name} {user?.last_name} • {schedule.name}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* Set Working Hours Button - Hidden (Manager cannot edit) */}
                  {false && user?.has_own_calendar && (
                    <button
                      onClick={() => setEditingWorkingHours(!editingWorkingHours)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                        editingWorkingHours
                          ? 'bg-orange-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {editingWorkingHours ? (
                        <>
                          <svg className="h-4 w-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Cancel Editing
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Set Working Hours
                        </>
                      )}
                    </button>
                  )}

                  {/* View Mode Toggle */}
                  <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('week')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                        viewMode === 'week'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Week
                    </button>
                    <button
                      onClick={() => setViewMode('month')}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                        viewMode === 'month'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Month
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Editing Mode Banner */}
        {editingWorkingHours && (
          <div className="bg-orange-50 border-b-2 border-orange-200">
            <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span className="text-orange-800 font-medium">
                    ⏰ Editing Working Hours Template
                  </span>
                </div>
                <span className="text-sm text-orange-700">
                  Click on <strong>Oct 21, 10:00</strong> → toggles <strong>only THIS date</strong> (exception to template)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {viewMode === 'week' ? (
            <div className="flex space-x-6">
              {/* Calendar View - 80% */}
              <div className="flex-1" style={{ width: '80%' }}>
                <WeeklyCalendar
                  schedule={schedule}
                  reservations={tasks}
                  selectedDate={selectedDate}
                  onSlotClick={handleSlotClick}
                  onDateChange={setSelectedDate}
                  editingWorkingHours={editingWorkingHours}
                  onTaskClick={handleTaskClick}
                />
              </div>

              {/* Monthly Calendar Sidebar - 20% */}
              <div style={{ width: '20%' }}>
                <MonthlyCalendar
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                />
              </div>
            </div>
          ) : (
            /* Month View - Full Width */
            <div>
              <MonthCalendarView
                schedule={schedule}
                reservations={tasks}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onDayClick={(date) => {
                  handleDateSelect(date);
                  setViewMode('week');
                }}
              />
            </div>
          )}
        </main>

        {/* Task Modal */}
        {showModal && selectedSlot && (
          <TaskModal
            schedule={schedule}
            selectedDate={selectedSlot.date}
            selectedTime={selectedSlot.time}
            onClose={() => {
              setShowModal(false);
              setSelectedSlot(null);
            }}
            onSubmit={handleReservationCreate}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Task</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the task with <strong>{selectedTask.client_name}</strong>?
                {selectedTask.google_calendar_event_id && (
                  <span className="block mt-2 text-sm text-gray-500">
                    This will also delete the event from Google Calendar.
                  </span>
                )}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedTask(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTask}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </>
  );
};

export default CalendarPage;

